// web/src/store/authSlice.ts
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { AuthState } from 'types/authTypes';

import { login, logout, verifySession } from './thunks';

const initialState: AuthState = {
  isAuthenticated: false,
  user: {
    userId: '',
    userName: '',
    fullName: '',
    firstName: '',
    currencyId: 1,
    sessionExpiry: '',
  },
  permissions: [],
  loading: false,
  sessionLoading: false,
  error: null,
  successMessage: null,
  errorMessage: null,
  resetRequired: false,
  authChecked: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearMessages(state) {
      state.successMessage = null;
      state.errorMessage = null;
    },
    setAuthChecked: (state, action: PayloadAction<boolean>) => {
      state.authChecked = action.payload;
    },
    updateUserCurrency: (state, action: PayloadAction<number>) => {
      state.user.currencyId = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Login
    builder.addCase(login.pending, (state) => {
      state.loading = true;
      state.errorMessage = null;
      state.successMessage = null;
      state.error = null;
    });
    builder.addCase(login.fulfilled, (state, action) => {
      if (action.payload.reset_required) {
        state.resetRequired = true;
      }
      if (action.payload.user) {
        state.isAuthenticated = true;
        // Include custom_route from the response root level into the user object
        state.user = {
          ...action.payload.user,
          custom_route: (action.payload as any).custom_route || null,
        };
        state.successMessage = 'Login Successful';
        state.resetRequired = action.payload.reset_required || false;
        state.error = null;
      }
      state.permissions = action.payload.permissions
        ? action.payload.permissions
        : [];
      state.loading = false;
    });
    builder.addCase(login.rejected, (state, action) => {
      state.isAuthenticated = false;
      state.error = action.payload || 'Login Failed';
      state.loading = false;
      state.errorMessage = action.payload || 'Login Failed';
      state.permissions = [];
    });

    // Logout
    builder.addCase(logout.pending, (state) => {
      state.loading = true;
      state.errorMessage = null;
      state.successMessage = null;
      state.error = null;
    });
    builder.addCase(logout.fulfilled, (state, action) => {
      state.isAuthenticated = false;
      state.user = initialState.user;
      state.loading = false;
      state.permissions = [];
      state.resetRequired = false;
      state.successMessage = action.payload.message;
    });
    builder.addCase(logout.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || 'Logout failed';
      state.errorMessage = 'Logout failed. Please try again.';
    });

    // Verify Session
    builder.addCase(verifySession.pending, (state) => {
      state.sessionLoading = true;
    });
    builder.addCase(verifySession.fulfilled, (state, action) => {
      state.resetRequired =
        !!action.payload.user && !!action.payload.reset_required;
      state.isAuthenticated = !!action.payload.user;
      // Include custom_route from the response root level into the user object
      state.user = action.payload.user
        ? {
            ...action.payload.user,
            custom_route: (action.payload as any).custom_route || null,
          }
        : initialState.user;
      state.successMessage = 'Session verified';
      state.sessionLoading = false;
      state.permissions = action.payload.permissions
        ? action.payload.permissions
        : [];
    });
    builder.addCase(verifySession.rejected, (state) => {
      state.isAuthenticated = false;
      state.user = initialState.user;
      state.sessionLoading = false;
      state.permissions = [];
    });
  },
});

export const { clearMessages, setAuthChecked, updateUserCurrency } =
  authSlice.actions;

export default authSlice.reducer;
